/**
 * Usage settings page — one-glance overview for the workspace admin.
 *
 * Two storage cards on top:
 *   - Current usage — active + trashed R2 bytes right now.
 *   - Peak last 30 days — the billing metric (peakLast30dBytes).
 * Both cards render "of 1 TB" as a soft cap literal (locked 2026-07-21 —
 * see project_workspace_settings_spec). Overuse is a display-only concept
 * here; enforcement is deferred to Phase 2 when per-workspace caps land.
 *
 * Two resource rows below:
 *   - Resources · projects count + Manage Projects link (→ settings/projects).
 *   - Users · members count + Manage Members link (→ settings/users).
 *
 * All numbers come from endpoints that already existed before this pass:
 *   GET /api/admin/storage/current   (Task 6, 2026-07-05)
 *   GET /api/admin/storage/peak      (Task 6, 2026-07-05)
 *   GET /api/editor/projects         (workspace projects, restriction-filtered)
 *   GET /api/admin/editors           (workspace members excluding ADMIN)
 */

import { Link } from "react-router-dom";
import { FolderKanban, HardDrive, TrendingUp, Users as UsersIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SettingsPageHeader } from "@/pages/settings/settings-placeholder";
import { useAuth } from "@/hooks/use-auth";
import { useProjects } from "@/hooks/use-projects";
import { useMembers } from "@/hooks/use-members";
import { useStorageCurrent, useStoragePeak } from "@/hooks/use-storage";

// 1 TB soft cap literal — Phase 2 replaces with users.storage_cap_bytes.
const SOFT_CAP_BYTES = 1024 ** 4;

function formatBytes(n: number | undefined | null): string {
  if (typeof n !== "number" || !Number.isFinite(n) || n < 0) return "—";
  if (n === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v < 10 ? v.toFixed(2) : v < 100 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

function formatDate(value: string | undefined | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function UsageSettingsPage() {
  const { role } = useAuth();
  const isAdmin = role === "ADMIN";

  const current = useStorageCurrent(isAdmin);
  const peak = useStoragePeak(isAdmin);
  const projectsQuery = useProjects();
  const membersQuery = useMembers(isAdmin);

  const projectCount = projectsQuery.data?.length ?? 0;
  // Members list doesn't include the admin themselves — +1 to reflect
  // the workspace owner in the total. Matches the Users page row count.
  const memberCount = (membersQuery.data?.length ?? 0) + (isAdmin ? 1 : 0);

  return (
    <div>
      <SettingsPageHeader
        title="Usage"
        subtitle="Storage, projects, and members at a glance."
      />

      {/* Storage cards */}
      <div className="grid gap-4 sm:grid-cols-2">
        <StorageCard
          icon={<HardDrive className="h-5 w-5" strokeWidth={1.75} />}
          label="Current usage"
          sublabel="Right now, across active + trash"
          bytes={current.data?.totalBytes ?? null}
          loading={current.isLoading}
          error={current.isError}
        />
        <StorageCard
          icon={<TrendingUp className="h-5 w-5" strokeWidth={1.75} />}
          label="Peak last 30 days"
          sublabel={
            peak.data?.peakDate
              ? `Highest was ${formatDate(peak.data.peakDate)} — billed on this`
              : "Rolling 30 days — billing metric"
          }
          bytes={peak.data?.peakLast30dBytes ?? null}
          loading={peak.isLoading}
          error={peak.isError}
        />
      </div>

      {/* Resources */}
      <section className="mt-10">
        <h2 className="mb-3 text-lg font-semibold text-foreground">Resources</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Monitor your projects and workspace members.
        </p>

        <div className="flex flex-col gap-3">
          <ResourceRow
            icon={<FolderKanban className="h-5 w-5" strokeWidth={1.75} />}
            label="Projects"
            count={projectsQuery.isLoading ? null : projectCount}
            unitSingular="project"
            unitPlural="projects"
            actionLabel="Manage Projects"
            actionTo="/admin/settings/projects"
          />
          <ResourceRow
            icon={<UsersIcon className="h-5 w-5" strokeWidth={1.75} />}
            label="Members"
            count={membersQuery.isLoading ? null : memberCount}
            unitSingular="member"
            unitPlural="members"
            actionLabel="Manage Members"
            actionTo="/admin/settings/users"
          />
        </div>
      </section>
    </div>
  );
}

// ── Storage card ─────────────────────────────────────────────────────────────

function StorageCard({
  icon,
  label,
  sublabel,
  bytes,
  loading,
  error,
}: {
  icon: React.ReactNode;
  label: string;
  sublabel: string;
  bytes: number | null;
  loading: boolean;
  error: boolean;
}) {
  const value = typeof bytes === "number" ? bytes : 0;
  // Progress cap at 100% even if we're over the soft cap — the overuse
  // details live on the Storage page.
  const fraction = Math.min(1, value / SOFT_CAP_BYTES);
  const over = value > SOFT_CAP_BYTES;

  return (
    <div className="rounded-2xl border border-border bg-secondary/40 p-6">
      <div className="mb-6 grid h-9 w-9 place-items-center rounded-lg bg-secondary text-muted-foreground">
        {icon}
      </div>
      <div className="text-sm font-medium text-foreground">{label}</div>
      <div className="mt-0.5 text-xs text-muted-foreground">{sublabel}</div>

      {loading ? (
        <div className="mt-4 h-6 w-40 animate-pulse rounded bg-secondary" />
      ) : error ? (
        <div className="mt-4 text-sm text-destructive">Couldn't load.</div>
      ) : (
        <>
          <div className="mt-4 flex items-baseline gap-1">
            <span className="text-2xl font-semibold tabular-nums text-foreground">
              {formatBytes(value)}
            </span>
            <span className="text-sm text-muted-foreground">
              &nbsp;of 1 TB
            </span>
          </div>
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-secondary">
            <div
              className={
                "h-full rounded-full transition-all " +
                (over ? "bg-destructive" : "bg-primary")
              }
              style={{ width: `${Math.max(2, fraction * 100)}%` }}
            />
          </div>
          {over && (
            <div className="mt-2 text-xs text-destructive">
              Over cap by {formatBytes(value - SOFT_CAP_BYTES)}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Resource row ─────────────────────────────────────────────────────────────

function ResourceRow({
  icon,
  label,
  count,
  unitSingular,
  unitPlural,
  actionLabel,
  actionTo,
}: {
  icon: React.ReactNode;
  label: string;
  count: number | null;
  unitSingular: string;
  unitPlural: string;
  actionLabel: string;
  actionTo: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-secondary/30 px-5 py-4">
      <div className="flex items-center gap-4">
        <div className="grid h-10 w-10 place-items-center rounded-lg bg-secondary text-muted-foreground">
          {icon}
        </div>
        <div>
          <div className="text-sm font-medium text-foreground">{label}</div>
          <div className="text-sm text-muted-foreground">
            {count === null
              ? "Loading…"
              : `${count} ${count === 1 ? unitSingular : unitPlural}`}
          </div>
        </div>
      </div>
      <Button asChild variant="outline" size="sm">
        <Link to={actionTo}>{actionLabel}</Link>
      </Button>
    </div>
  );
}
